import React, { useMemo, useState } from "react";
import { observer } from "mobx-react";
import { Box, Paper, Tooltip, useTheme } from "@mui/material";
import CustomTypography from "../../../../../../views/components/common/form/CustomTypography";
import { PodRevenueDashboardStore } from "../../../../stores/dashboards/PodRevenueDashboardStore";
import { formatPodMoney, formatPodShare } from "./podRevenueFormat";
import { PodRevenueSectionLabel, PodRevenueSkeletonCard } from "./PodRevenueSection";
export type PodMixKey = "selling" | "handling" | "shipping";

interface Props {
  store: PodRevenueDashboardStore;
  activeKey: PodMixKey | null;
  onActiveChange: (key: PodMixKey | null) => void;
}

const PodRevenueMix: React.FC<Props> = observer(({ store, activeKey, onActiveChange }) => {
  const theme = useTheme();
  const colors = theme.palette.customColors;
  const mix = store.summary?.data.mix;
  const gross = store.summary?.data.kpis.gross_revenue || 0;
  const [hoverKey, setHoverKey] = useState<PodMixKey | null>(null);
  const focus = hoverKey ?? activeKey;

  const kpis = store.summary?.data.kpis;
  const rows = useMemo(() => {
    if (!mix || !kpis) return [];
    return [
      {
        key: "selling" as PodMixKey,
        label: "Selling (incl. GST)",
        value: kpis.selling_inclusive ?? mix.selling_exclusive,
        color: colors.darkBlue,
        inBar: true,
      },
      {
        key: "shipping" as PodMixKey,
        label: "Shipping (incl. GST)",
        value: kpis.shipping_inclusive ?? mix.shipping_amount,
        color: colors.purple,
        inBar: true,
      },
      {
        key: "handling" as PodMixKey,
        label: "Handling (incl. GST)",
        value: kpis.handling_inclusive ?? mix.handling_amount,
        color: colors.successGreen,
        inBar: true,
      },
    ];
  }, [mix, kpis, colors.darkBlue, colors.successGreen, colors.purple]);

  if (!store.summary) {
    return store.summaryLoading ? (
      <>
        <PodRevenueSectionLabel label="Revenue mix" />
        <PodRevenueSkeletonCard height={130} />
      </>
    ) : null;
  }
  if (!mix) return null;

  const dim = (key: PodMixKey) => (focus && focus !== key ? 0.35 : 1);
  const toggle = (key: PodMixKey) => onActiveChange(activeKey === key ? null : key);

  return (
    <>
      <PodRevenueSectionLabel
        label="Revenue mix"
        hint="Incl. GST view — same Selling, Shipping, and Handling as above, but with their GST added. Bar slices add up to Gross."
        action={
          activeKey ? (
            <CustomTypography
              variant="heading8"
              fontWeight={600}
              color={colors.darkBlue}
              sx={{ cursor: "pointer", "&:hover": { textDecoration: "underline" } }}
              onClick={() => onActiveChange(null)}
            >
              Clear highlight
            </CustomTypography>
          ) : undefined
        }
      />
      <Paper
        variant="outlined"
        sx={{ p: 2, borderRadius: 2, borderColor: colors.softCardBorder, bgcolor: colors.softCardBackground }}
      >
        <Box
          sx={{ display: "flex", height: 18, borderRadius: 999, overflow: "hidden", bgcolor: colors.lightGray, mb: 2, gap: "2px" }}
          onMouseLeave={() => setHoverKey(null)}
        >
          {rows.filter((row) => row.inBar).map((row) => {
            const width = gross > 0 ? Math.max((row.value / gross) * 100, row.value > 0 ? 2 : 0) : 0;
            if (width <= 0) return null;
            return (
              <Tooltip
                key={row.key}
                arrow
                placement="top"
                title={`${row.label}: ${formatPodMoney(row.value)} · ${formatPodShare(row.value, gross)} of gross`}
              >
                <Box
                  onMouseEnter={() => setHoverKey(row.key)}
                  onClick={() => toggle(row.key)}
                  sx={{
                    width: `${width}%`,
                    bgcolor: row.color,
                    cursor: "pointer",
                    opacity: dim(row.key),
                    transition: "opacity .15s ease, filter .15s ease",
                    "&:hover": { filter: "brightness(1.08)" },
                  }}
                />
              </Tooltip>
            );
          })}
        </Box>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))", md: "repeat(3, minmax(0, 1fr))" },
            gap: 1.5,
          }}
        >
          {rows.map((row) => {
            const active = activeKey === row.key;
            return (
              <Box
                key={row.key}
                role="button"
                tabIndex={0}
                aria-pressed={active}
                onClick={() => toggle(row.key)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    toggle(row.key);
                  }
                }}
                onMouseEnter={() => setHoverKey(row.key)}
                onMouseLeave={() => setHoverKey(null)}
                sx={{
                  p: 1.25,
                  borderRadius: 1.5,
                  cursor: "pointer",
                  border: "1px solid",
                  borderColor: active ? row.color : "transparent",
                  bgcolor: active ? colors.white : "transparent",
                  opacity: dim(row.key),
                  transition: "all .15s ease",
                  "&:hover": { bgcolor: colors.white, borderColor: colors.softCardBorder },
                }}
              >
                <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                  <Box sx={{ width: 10, height: 10, borderRadius: "50%", bgcolor: row.color }} />
                  <CustomTypography variant="heading8" fontWeight={500} color={colors.mutedSlateText}>
                    {row.label}
                  </CustomTypography>
                </Box>
                <CustomTypography variant="heading5" fontWeight={700} color={colors.navyText} sx={{ fontVariantNumeric: "tabular-nums" }}>
                  {formatPodMoney(row.value)}
                </CustomTypography>
                <CustomTypography variant="heading8" fontWeight={400} color={colors.greyTextColor}>
                  {formatPodShare(row.value, gross)} of gross
                </CustomTypography>
              </Box>
            );
          })}
        </Box>
      </Paper>
    </>
  );
});

export default PodRevenueMix;
